/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import Axios from 'axios';
import {Machine, assign} from 'xstate';

import {ExtJS} from 'nexus-ui-plugin';

import UIStrings from '../../../../constants/UIStrings';

export default Machine(
    {
      initial: 'loading',

      context: {
        data: {
          enabled: false,
          realmActive: false,
          applicationName: '',
          applicationPassword: '',
          url: '',
          useTrustStoreForUrl: false,
          timeout: ''
        },
        pristineData: {},
        isPristine: true,
        isEdited: false,
        isValid: true,
        isInvalid: false,
        isCrowdServerUrlValid: false,
        error: null
      },

      states: {
        loading: {
          invoke: {
            id: 'fetchData',
            src: 'fetchData',
            onDone: {
              target: 'fetched',
              actions: ['setData', 'checkEdited', 'validate']
            },
            onError: {
              target: 'error',
              actions: ['logLoadError']
            }
          }
        },
        fetched: {
          on: {
            'UPDATE': {
              actions: ['update', 'checkEdited', 'validate']
            },
            'DISCARD': {
              actions: ['discard', 'checkEdited', 'validate']
            },
            'SAVE': {
              target: 'saving'
            },
            'VERIFY-CONNECTION': {
              target: 'verifyingConnection'
            },
            'CLEAR-CACHE': {
              target: 'clearingCache'
            }
          }
        },
        saving: {
          invoke: {
            id: 'saveData',
            src: 'saveData',
            onDone: {
              target: 'loading',
              actions: ['logSaveSuccess', 'clearError']
            },
            onError: {
              target: 'error',
              actions: ['logSaveError']
            }
          }
        },
        verifyingConnection: {
          invoke: {
            id: 'verifyConnection',
            src: 'verifyConnection',
            onDone: {
              target: 'loading',
              actions: ['logConnectionSuccess', 'clearError']
            },
            onError: {
              target: 'fetched',
              actions: ['setError', 'logConnectionError']
            }
          }
        },
        clearingCache: {
          invoke: {
            id: 'clearCache',
            src: 'clearCache',
            onDone: {
              target: 'loading',
              actions: ['logClearCacheSuccess', 'clearError']
            },
            onError: {
              target: 'fetched',
              actions: ['setError', 'logClearCacheError']
            }
          }
        },
        error: {
          type: 'final'
        }
      }
    },
    {
      actions: {
        setData: assign({
          data: (context, event) => {
            return event.data.data;
          },
          pristineData: (context, event) => {
            return event.data.data;
          },
          roles: (context, event) => {
            return event.data.data;
          }
        }),
        update: assign({
          data: (_, {data}) => data
        }),
        checkEdited: assign(({data, pristineData}) => {
          const isPristine = Object.keys(data).every((key) => data[key] === pristineData[key]);
          return {
            isPristine,
            isEdited: !isPristine
          }
        }),
        validate: assign(({data}) => {
          const uriRegex = /^https?:\/\/.+$/i;
          const hasRequiredFields = data.applicationName && data.applicationPassword && data.url;
          const isCrowdServerUrlValid = uriRegex.test(data.url);
          return {
            isInvalid: !hasRequiredFields || !isCrowdServerUrlValid,
            isCrowdServerUrlValid: isCrowdServerUrlValid
          };
        }),
        discard: assign({
          data: ({pristineData}) => pristineData
        }),
        logLoadError: ({error}) => {
          if (error) {
            console.error(error);
          }
          ExtJS.showErrorMessage(UIStrings.CROWD_SETTINGS.MESSAGES.LOAD_ERROR);
        },
        logSaveSuccess: () => {
          ExtJS.showSuccessMessage(UIStrings.CROWD_SETTINGS.MESSAGES.SAVE_SUCCESS);
        },
        logSaveError: ({error}) => {
          if (error) {
            console.error(error);
          }
          ExtJS.showErrorMessage(UIStrings.CROWD_SETTINGS.MESSAGES.SAVE_ERROR);
        },
        logConnectionError: ({error}) => {
          ExtJS.showErrorMessage(UIStrings.CROWD_SETTINGS.MESSAGES.VERIFY_CONNECTION_ERROR + `${error ? ': ' + error : ''}`);
        },
        logConnectionSuccess: () => {
          ExtJS.showSuccessMessage(UIStrings.CROWD_SETTINGS.MESSAGES.VERIFY_CONNECTION_SUCCESS);
        },
        logClearCacheError: ({error}) => {
          ExtJS.showErrorMessage(UIStrings.CROWD_SETTINGS.MESSAGES.CLEAR_CACHE_ERROR + `${error ? ': ' + error : ''}`);
        },
        logClearCacheSuccess: () => {
          ExtJS.showSuccessMessage(UIStrings.CROWD_SETTINGS.MESSAGES.CLEAR_CACHE_SUCCESS);
        },
        setError: assign({
          error: (_, event) => event.data.response.data
        }),
        clearError: assign({
          error: () => undefined
        })
      },
      services:
          {
            fetchData: () => {
              return Axios.get('/service/rest/beta/atlassian-crowd');
            },
            saveData: ({data}) => {
              return Axios.put('/service/rest/beta/atlassian-crowd', {
                ...data
              });
            },
            verifyConnection: ({data}) => {
              return Axios.post('/service/rest/beta/atlassian-crowd/verify-connection', {
                ...data
              });
            },
            clearCache: () => {
              return Axios.post('/service/rest/beta/atlassian-crowd/clear-cache');
            }
          }
    }
);
